import cx from 'classnames';
import * as CyrillicToTranslit from 'cyrillic-to-translit-js';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { DownloadIcon } from 'components';
import type { PaymentLayoutAction } from 'components/PaymentLayout/PaymentLayout';
import { EventName, PaymentType } from 'enums';
import { useGatewayAppConfigs, useOrderContext } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { createQrGalleryImage, eventUtils, saveImage } from 'utils';

import { getRosaOneSaveUrl } from './rosa-one-save-source.utils';
import { RosaOneWrapper } from './RosaOneWrapper';
import { ROSA_ONE_PALETTE } from '../../constants/rosa-one-palette.constant';
import type { RosaOneCollapseState } from '../../types';
import { RosaOneButton } from '../RosaOneButton';
import { RosaOneFieldsList } from '../RosaOneFieldsList';
import type { MobileStep } from '../RosaOnePaymentDetails/RosaOnePaymentDetails';
import { RosaOneReadyPanel } from '../RosaOneReadyPanel';

const cyrillicToTranslit = (CyrillicToTranslit as any)();

const CONFIRM_BUTTON_DELAY_MS = 10_000;

type RosaOneRequisitesDetailsProps = {
  confirmAction?: PaymentLayoutAction;
  onStepChange?: (step: MobileStep) => void;
  className?: string;
};

export const RosaOneRequisitesDetails: React.FC<
  RosaOneRequisitesDetailsProps
> = ({ confirmAction, onStepChange, className }) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.rosa_one',
  });

  const { data: gatewayConfigs, isLoading: isGatewayConfigsLoading } =
    useGatewayAppConfigs();

  const {
    order,
    currencySymbol,
    formattedPaymentFields,
    requisites,
    paymentType,
    paymentTypeFlags: { qrCodeUrl },
  } = useOrderContext();

  const [qrDataUrl, setQrDataUrl] = useState<string>();
  const [qrImageLoaded, setQrImageLoaded] = useState(false);
  const [qrSaved, setQrSaved] = useState(false);
  const [qrOpened, setQrOpened] = useState(false);
  const [saveError, setSaveError] = useState(false);

  const [qrCollapseState, setQrCollapseState] =
    useState<RosaOneCollapseState>('auto');
  const closeQrTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const [detailsCollapseState, setDetailsCollapseState] =
    useState<RosaOneCollapseState>('auto');
  const closeDetailsTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const isMobileViewport = useMemo(
    () => window.matchMedia('(max-width: 767px)').matches,
    [],
  );

  const savedPanelRef = useRef<HTMLDivElement>(null);

  const [confirmVisible, setConfirmVisible] = useState(false);
  const confirmTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const startConfirmTimer = useCallback(() => {
    if (confirmTimerRef.current) {
      return;
    }
    confirmTimerRef.current = setTimeout(
      () => setConfirmVisible(true),
      CONFIRM_BUTTON_DELAY_MS,
    );
  }, []);

  useEffect(
    () => () => {
      clearTimeout(closeQrTimerRef.current);
      clearTimeout(closeDetailsTimerRef.current);
      clearTimeout(confirmTimerRef.current);
      confirmTimerRef.current = undefined;
    },
    [],
  );

  useEffect(() => {
    if (window.matchMedia('(min-width: 768px)').matches) {
      startConfirmTimer();
    }
  }, [startConfirmTimer]);

  useEffect(() => {
    if (!qrSaved) {
      return;
    }
    savedPanelRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, [qrSaved]);

  useEffect(() => {
    if (!qrOpened) {
      return;
    }
    // Wait for the QR unfold animation to finish so the target position is stable.
    const timer = setTimeout(() => {
      savedPanelRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 450);
    return () => clearTimeout(timer);
  }, [qrOpened]);

  useEffect(() => {
    if (!window.matchMedia('(max-width: 767px)').matches) {
      return;
    }
    // Sync state with CSS auto-close animation (1500ms delay + 720ms animation)
    const timer = setTimeout(() => {
      setQrCollapseState((current) =>
        current === 'auto' ? 'closed' : current,
      );
    }, 2220);
    return () => clearTimeout(timer);
  }, []);

  const transferUrl = useMemo(() => {
    if (!requisites) {
      return undefined;
    }

    const receiverName = (
      requisites.cardholder
        ? cyrillicToTranslit.transform(requisites.cardholder)
        : ''
    ).toUpperCase();
    const amount = String(order.amount);

    if (paymentType === PaymentType.TCard2Card) {
      const cardNumber = requisites.cardInfo?.replace(/\D/g, '') ?? '';
      if (!cardNumber) {
        return undefined;
      }
      return `/transfers/abroad?cardNumber=${encodeURIComponent(
        cardNumber,
      )}&receiverName=${encodeURIComponent(
        receiverName,
      )}&amount=${encodeURIComponent(amount)}`;
    } else if (
      paymentType === PaymentType.NSPK ||
      paymentType === PaymentType.TSBP
    ) {
      const phone = requisites.phone?.replace(/\D/g, '') ?? '';
      if (!phone) {
        return undefined;
      }
      const bankBic = requisites.bank
        ? gatewayConfigs?.sberQrBankBic?.[requisites.bank]
        : undefined;

      if (!bankBic) {
        return undefined;
      }

      const bankParam = bankBic ? `&bank=${encodeURIComponent(bankBic)}` : '';
      return `/transfers/abroad?phoneNumber=${encodeURIComponent(
        phone,
      )}${bankParam}&amount=${encodeURIComponent(
        amount,
      )}&receiverName=${encodeURIComponent(receiverName)}`;
    } else {
      return undefined;
    }
  }, [requisites, order.amount, paymentType, gatewayConfigs?.sberQrBankBic]);

  const hasQrSource = !!(transferUrl || qrCodeUrl);
  const isLoadingQrSource = isGatewayConfigsLoading && !qrCodeUrl;
  const saveQrUrl = getRosaOneSaveUrl({ transferUrl, qrDataUrl, qrCodeUrl });
  const qrReady = transferUrl ? !!saveQrUrl : !!saveQrUrl && qrImageLoaded;

  const handleToggleQr = useCallback(() => {
    if (qrCollapseState === 'open' || qrCollapseState === 'auto') {
      clearTimeout(closeQrTimerRef.current);
      setQrCollapseState('closing');
      closeQrTimerRef.current = setTimeout(
        () => setQrCollapseState('closed'),
        400,
      );
    } else {
      clearTimeout(closeQrTimerRef.current);
      setQrCollapseState('open');
      setQrOpened(true);
      startConfirmTimer();
      onStepChange?.('pay');
    }
  }, [qrCollapseState, startConfirmTimer, onStepChange]);

  const handleToggleDetails = useCallback(() => {
    if (detailsCollapseState === 'open') {
      clearTimeout(closeDetailsTimerRef.current);
      setDetailsCollapseState('closing');
      closeDetailsTimerRef.current = setTimeout(
        () => setDetailsCollapseState('closed'),
        400,
      );
    } else {
      clearTimeout(closeDetailsTimerRef.current);
      setDetailsCollapseState('open');
    }
  }, [detailsCollapseState]);

  const handleSave = useCallback(async () => {
    if (!saveQrUrl) {
      return;
    }
    setSaveError(false);
    try {
      const d = new Date();
      const pad = (n: number) => String(n).padStart(2, '0');
      const stamp = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(
        d.getDate(),
      )}_${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
      const galleryUrl = await createQrGalleryImage(saveQrUrl);
      await saveImage(galleryUrl, `rosa-one-${stamp}.jpeg`);
      eventUtils.trackEvent({
        event: EventName.SaveQr,
      });
      setQrSaved(true);
      startConfirmTimer();
      onStepChange?.('pay');
    } catch {
      setSaveError(true);
    }
  }, [saveQrUrl, onStepChange, startConfirmTimer]);

  return (
    <>
      <section
        style={{ borderColor: ROSA_ONE_PALETTE.line }}
        className={cx(
          'tw-border tw-border-solid tw-rounded-[32px] tw-px-[34px] tw-pt-[28px] tw-pb-[25px] tw-bg-[linear-gradient(145deg,rgba(22,43,61,.98),rgba(10,26,40,.99))] tw-shadow-[0_24px_80px_rgba(0,0,0,.42)]',
          className,
        )}
      >
        <div className="tw-flex tw-justify-between tw-items-baseline tw-mb-4">
          <span
            style={{ color: ROSA_ONE_PALETTE.muted }}
            className="tw-text-[13px]"
          >
            {t('amount_label')}
          </span>
          <strong
            className="tw-text-[30px] tw-tracking-[-0.04em]"
            style={{ color: ROSA_ONE_PALETTE.text }}
          >
            {order.amount}{' '}
            <small className=" tw-text-[19px]">{currencySymbol}</small>
          </strong>
        </div>

        {hasQrSource && (
          <RosaOneButton
            primary={!qrSaved}
            disabled={!qrReady}
            onClick={handleSave}
            label={qrSaved ? t('qr_saved') : t('save_qr')}
            className="md:tw-hidden tw-w-full tw-max-w-[360px] tw-mx-auto tw-min-h-[50px] tw-rounded-[17px] tw-text-[15px]"
          >
            <DownloadIcon />
          </RosaOneButton>
        )}
        {saveError && (
          <p
            className="tw-text-center tw-text-[13px] tw-mt-2 tw-mb-0 md:tw-hidden"
            style={{ color: '#f87171' }}
          >
            {t('save_qr_error')}
          </p>
        )}

        <RosaOneWrapper
          collapseState={qrCollapseState}
          hasQrSource={hasQrSource}
          isLoadingQrSource={isLoadingQrSource}
          qrReady={qrReady}
          showReadyText={!isMobileViewport || qrOpened}
          transferUrl={transferUrl}
          qrCodeUrl={qrCodeUrl}
          onQrGenerated={setQrDataUrl}
          onImageLoad={() => setQrImageLoaded(true)}
        />

        <RosaOneButton
          onClick={handleToggleQr}
          label={
            qrCollapseState === 'open' || qrCollapseState === 'auto'
              ? t('hide_qr')
              : t('show_qr')
          }
          className="md:tw-hidden tw-w-full tw-max-w-[360px] tw-mx-auto tw-mt-3 tw-mb-1 tw-py-[11px] tw-rounded-[14px] tw-text-[14px]"
        >
          <span
            style={{ color: ROSA_ONE_PALETTE.text }}
            className="tw-text-[16px] tw-leading-none"
          >
            {'◉'}
          </span>
        </RosaOneButton>
        {!qrSaved && (
          <p
            style={{ color: ROSA_ONE_PALETTE.muted }}
            className="md:tw-hidden tw-text-center tw-text-[11px] tw-mt-[6px] tw-mb-0"
          >
            {t('save_hint')}
          </p>
        )}

        {hasQrSource && (
          <RosaOneButton
            primary={!qrSaved}
            disabled={!qrReady}
            onClick={handleSave}
            label={qrSaved ? t('qr_saved') : t('save_qr')}
            className="tw-hidden md:tw-flex tw-w-full tw-max-w-[360px] tw-mx-auto tw-min-h-[50px] tw-rounded-[17px] tw-text-[15px]"
          >
            <DownloadIcon />
          </RosaOneButton>
        )}
        {saveError && (
          <p
            className="tw-text-center tw-text-[13px] tw-mt-2 tw-mb-0 tw-hidden md:tw-block"
            style={{ color: '#f87171' }}
          >
            {t('save_qr_error')}
          </p>
        )}

        <RosaOneFieldsList
          fields={formattedPaymentFields}
          collapseState={detailsCollapseState}
          onToggle={handleToggleDetails}
        />

        {confirmAction && confirmVisible && (
          <RosaOneButton
            primary
            onClick={confirmAction.onClick}
            label={confirmAction.label}
            className="tw-hidden md:tw-flex tw-w-full tw-mt-6 tw-px-6 tw-py-3 tw-rounded-[14px] tw-text-[14px]"
          />
        )}
      </section>

      {(qrSaved || qrOpened) && (
        <div ref={savedPanelRef}>
          <RosaOneReadyPanel
            onStepChange={onStepChange}
            confirmAction={confirmAction}
            confirmVisible={confirmVisible}
          />
        </div>
      )}
    </>
  );
};
